import { zeroAddress } from 'viem';

import type { ChainKey } from '@infinex/chain-config';

import type { SupportLevel } from './actions';
import { parseChainAddress } from './assets';
import type { ChainAssetFlags } from './flags';
import type { AssetGroupIdString, ChainAsset, GroupedAsset } from './groups';
import type { SocialLinks } from './socials';

export type AssetMetadataV2Lookup =
  | { assetId: string }
  | { chain: ChainKey; address: string };

export type AssetMetadataV2Flags = ChainAssetFlags & {
  supportLevel: SupportLevel;
  protocolId: string | null;
};

export type AssetMetadataV2 = GroupedAsset & {
  flags: Partial<Record<ChainKey, AssetMetadataV2Flags>>;
  /** true if ANY member has isOfficial=true in flags */
  isOfficial: boolean;
  coingeckoId?: string;
  primaryChain?: ChainKey;
  stableRootAsset?: string;
  socials?: SocialLinks;
};

/** Grouped V2 metadata keyed by {@link AssetGroupIdString}. */
export type AssetsByGroupId = Partial<
  Record<AssetGroupIdString, AssetMetadataV2>
>;

export const normalizeNativeEvmAssetAddress = (address: string) =>
  address.toLowerCase() === zeroAddress ? 'native' : address;

/**
 * Canonical cache key for an `AssetMetadataV2Lookup`. Used by the server's
 * V2 metadata Redis cache and by clients that need to index into a batch
 * response (`GetAssetMetadataBatchOutput`).
 *
 * `assetId` lookups key by the raw id; chain+address lookups key by
 * `${chain}:${parsedAddress}` — the address is normalised via
 * `parseChainAddress` so callers can pass un-normalised input safely.
 */
export function makeCacheKey(lookup: AssetMetadataV2Lookup): string {
  if ('assetId' in lookup) return lookup.assetId;
  return `${lookup.chain}:${parseChainAddress(lookup.address)}`;
}

/**
 * Resolve a required chain member from V2 group metadata. Callers that used
 * to rely on the deleted V1 converter's "Member not found" throw should use
 * this instead of reading `members[chain]` and failing silently.
 */
export function requireChainMember(
  metadata: AssetMetadataV2,
  chain: ChainKey
): ChainAsset {
  const member = metadata.members[chain];
  if (!member) {
    throw new Error(
      `Member not found for chain ${chain} in asset metadata (group: ${JSON.stringify(metadata.groupId)})`
    );
  }
  return member;
}

/**
 * Per KTD2, identity comparisons (patron NFT / CryptoPunks / EtherRocks /
 * near-intents asset ids, etc.) key off the group's `assetId`, independent of
 * officialness — a flag-only official asset can still be grouped by
 * chain+address and has no `assetId` to compare against.
 */
export function groupAssetId(metadata: AssetMetadataV2): string | undefined {
  return 'assetId' in metadata.groupId ? metadata.groupId.assetId : undefined;
}
